import { useState, useEffect, useCallback } from "react";
import { FileWarning, Download, Eye, ChevronDown, ChevronUp, Trash2, Loader2, Search, Filter } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RISK_COLORS = {
  high: "bg-orange-100 text-orange-800",
  danger: "bg-red-100 text-red-800",
  "extreme-danger": "bg-red-200 text-red-900",
};

const STATUS_COLORS = {
  received: "bg-yellow-100 text-yellow-800",
  in_review: "bg-blue-100 text-blue-800",
  resources_assigned: "bg-green-100 text-green-800",
  follow_up: "bg-purple-100 text-purple-800",
  closed: "bg-gray-100 text-gray-600",
};

const RESOURCE_OPTIONS = [
  "Emergency Shelter", "Safety Planning", "Protective Order Assistance",
  "Counseling Referral", "Legal Aid", "Housing Assistance",
  "Financial Assistance", "Childcare Services", "Medical Referral",
  "Transportation", "Job Readiness Program", "Support Group",
];

export const LAPReportsPanel = ({ getAuthHeader }) => {
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filterStatus, setFilterStatus] = useState("");
  const [search, setSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [reportsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/lap-reports`, getAuthHeader()),
        axios.get(`${API}/admin/lap-reports/stats/summary`, getAuthHeader()),
      ]);
      setReports(reportsRes.data);
      setStats(statsRes.data);
    } catch {
      toast.error("Failed to load LAP reports");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const updateStatus = async (id, status, resources, notes) => {
    try {
      await axios.put(`${API}/admin/lap-reports/${id}/status`, {
        status,
        resources_assigned: resources || undefined,
        admin_notes: notes || undefined,
      }, getAuthHeader());
      toast.success(`Status updated to ${status.replace("_", " ")}`);
      fetchData();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const deleteReport = async (id) => {
    if (!window.confirm("Delete this LAP report permanently?")) return;
    try {
      await axios.delete(`${API}/admin/lap-reports/${id}`, getAuthHeader());
      toast.success("Report deleted");
      fetchData();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.append("status", filterStatus);
      const res = await axios.get(`${API}/admin/lap-reports/export/csv?${params}`, {
        ...getAuthHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `lap_reports_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      toast.success("CSV exported");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const filtered = reports.filter((r) => {
    if (filterStatus && r.status !== filterStatus) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        r.victim_name?.toLowerCase().includes(q) ||
        r.department_name?.toLowerCase().includes(q) ||
        r.submitted_by?.toLowerCase().includes(q)
      );
    }
    return true;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-6 h-6 animate-spin text-warm-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="lap-reports-panel">
      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: "Total", value: stats.total, color: "text-gray-900" },
            { label: "Received", value: stats.received, color: "text-yellow-700" },
            { label: "In Review", value: stats.in_review, color: "text-blue-700" },
            { label: "Resources Assigned", value: stats.resources_assigned, color: "text-green-700" },
            { label: "Closed", value: stats.closed, color: "text-gray-500" },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-gray-100 rounded-xl p-3 text-center">
              <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
        <div className="flex gap-2 items-center flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              data-testid="lap-search"
              className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
              placeholder="Search victim, department..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            data-testid="lap-filter-status"
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="received">Received</option>
            <option value="in_review">In Review</option>
            <option value="resources_assigned">Resources Assigned</option>
            <option value="follow_up">Follow Up</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <Button
          size="sm"
          onClick={exportCSV}
          disabled={exporting}
          data-testid="lap-export-csv"
          className="bg-warm-700 hover:bg-warm-800 text-white gap-1"
        >
          {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Export CSV
        </Button>
      </div>

      {/* Reports List */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <FileWarning className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p>No LAP reports found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              expanded={expanded === r.id}
              onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
              onUpdateStatus={updateStatus}
              onDelete={deleteReport}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ReportCard = ({ report: r, expanded, onToggle, onUpdateStatus, onDelete }) => {
  const [resources, setResources] = useState(r.resources_assigned || []);
  const [notes, setNotes] = useState(r.admin_notes || "");

  const toggleResource = (res) => {
    setResources((prev) =>
      prev.includes(res) ? prev.filter((x) => x !== res) : [...prev, res]
    );
  };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid={`lap-report-${r.id}`}>
      <div
        className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${RISK_COLORS[r.risk_level] || "bg-gray-100"}`}>
            {r.risk_level?.replace("-", " ").toUpperCase()}
          </span>
          <span className="font-medium text-sm text-gray-900 truncate">{r.victim_name}</span>
          <span className="text-xs text-gray-400 hidden sm:inline">
            {r.department_name && `from ${r.department_name}`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[r.status] || "bg-gray-100"}`}>
            {r.status?.replace("_", " ")}
          </span>
          <span className="text-xs text-gray-400 hidden sm:inline">{r.incident_date}</span>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </div>

      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50/50">
          {/* Victim & Incident Info */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Victim Information</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Name:</span> {r.victim_name}</p>
                <p><span className="text-gray-500">Phone:</span> {r.victim_phone || "N/A"}</p>
                <p><span className="text-gray-500">Age:</span> {r.victim_age || "N/A"}</p>
                <p><span className="text-gray-500">Gender:</span> {r.victim_gender || "N/A"}</p>
                <p><span className="text-gray-500">Address:</span> {r.victim_address || "N/A"}</p>
              </div>
            </div>
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Incident Details</h4>
              <div className="space-y-1 text-sm">
                <p><span className="text-gray-500">Date:</span> {r.incident_date}</p>
                <p><span className="text-gray-500">Location:</span> {r.incident_location || "N/A"}</p>
                <p><span className="text-gray-500">Abuser:</span> {r.abuser_name || "N/A"} ({r.abuser_relationship || "N/A"})</p>
                <p><span className="text-gray-500">Score:</span> {r.lethality_score ?? "N/A"}</p>
                <p><span className="text-gray-500">Children:</span> {r.children_involved || 0}</p>
                <p><span className="text-gray-500">Weapons:</span> {r.weapons_involved ? "Yes" : "No"}</p>
                <p><span className="text-gray-500">Prior incidents:</span> {r.prior_incidents ? "Yes" : "No"}</p>
                <p><span className="text-gray-500">Protective order:</span> {r.protective_order ? "Yes" : "No"}</p>
              </div>
            </div>
          </div>

          {r.incident_description && (
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Description</h4>
              <p className="text-sm text-gray-700 bg-white p-3 rounded-lg border">{r.incident_description}</p>
            </div>
          )}

          {/* Submitted by */}
          <div className="text-xs text-gray-400">
            Submitted by {r.submitted_by} ({r.submitted_by_org}) on {new Date(r.created_at).toLocaleString()}
          </div>

          {/* Resource Assignment */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">Assign Resources</h4>
            <div className="flex flex-wrap gap-2">
              {RESOURCE_OPTIONS.map((res) => (
                <button
                  key={res}
                  onClick={() => toggleResource(res)}
                  className={`px-3 py-1 rounded-full text-xs border transition-colors ${
                    resources.includes(res)
                      ? "bg-warm-100 border-warm-400 text-warm-800"
                      : "bg-white border-gray-200 text-gray-600 hover:border-warm-300"
                  }`}
                >
                  {res}
                </button>
              ))}
            </div>
          </div>

          {/* Admin Notes */}
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase mb-1">Admin Notes</h4>
            <textarea
              className="w-full border border-gray-200 rounded-lg p-2 text-sm"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Internal notes..."
            />
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 pt-2">
            {["received", "in_review", "resources_assigned", "follow_up", "closed"].map((s) => (
              <Button
                key={s}
                size="sm"
                variant={r.status === s ? "default" : "outline"}
                className={r.status === s ? "bg-warm-700 text-white" : "text-xs"}
                onClick={() => onUpdateStatus(r.id, s, resources, notes)}
              >
                {s.replace("_", " ")}
              </Button>
            ))}
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:bg-red-50 ml-auto"
              onClick={() => onDelete(r.id)}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
